(ns app.front.view.inspections.edit
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.components :as uic]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.select :as ui-select]
            [app.front.view.group.subs :as group-subs]
            [app.front.view.inspections.subs :as inspection-sub]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [clojure.data :as cd]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [reagent.core :as r]))


(defn remove-button [{:keys [tr target-entity on-remove]
                      :or   {on-remove (fn [_])}}
                     {id  :db/id
                      :as inspection}]
  (let [remove-signal [:process.signal/remove-inspection {:inspection-id id}]
        lang          (<sub [:language])]
    (if (<sub [::process/signal-processing? remove-signal])
      (cc/loading-spinner {:with-overlay false :small true})
      (when (<sub [::process/signal-permitted? target-entity remove-signal])
        [ui/modal-confirmation-button
         {:tr                  tr
          :id                  "remove-inspection"
          :trigger-button-opts {:icon i/trash
                                :kind :tertiary}
          :dangerous?          true
          :trigger-button-text (tr [:remove])
          :on-yes-click        (fn [_]
                                 (on-remove id)
                                 (>evt [::process/signal target-entity remove-signal]))
          :yes-button-text     (tr [:inspection/remove-inspection])}
         [:p
          [:span (tr [:inspection/removing])]
          [:span.italic (inspection-model/inspection-name inspection lang)]]]))))


(defn update-signal-fn [{id :db/id}
                        attr]
  (fn [value]
    [:process.signal/update-inspection
     {:inspection-id id
      :attr          attr
      :value         (if (= attr :inspection/inspector-qualifications)
                       (du/entity-id-set value)
                       value)}]))


(defn editor-props-base [{:keys [tr]}
                         {id :db/id :as inspection}
                         attr]
  {:value      (get inspection attr)
   :id         (str attr "-" id)
   :label-text (tr [attr])})


(defn i18n-editor-props [{:keys [on-update tr target-entity]}
                         *inspection
                         attr
                         & [value-fn]]
  (let [{inspection-id :db/id
         :as           inspection} @*inspection
        ev->val                    (or value-fn identity)
        ->update-signal            (update-signal-fn inspection attr)]
    {:multiline?          true
     :on-change           #(swap! *inspection assoc attr (ev->val %))
     :on-end-edit         (fn [ev]
                            (let [new-value (ev->val ev)]
                              (on-update inspection-id attr new-value)))
     :disabled            (not (<sub [::process/signal-permitted? target-entity (->update-signal nil)]))
     :strings             (get inspection attr)
     :id                  (str attr "-" inspection-id)
     :label-text          (tr [attr])
     :allowed-locales-sub ::ws-subs/allowed-locales}))

(defn empty-or-= [reference-set some-set]
  (or (empty? some-set)
      (= reference-set some-set)))


(defn qualification-selector-v2 [tr *inspection authority-group-id can-edit? update-fn]
  (let [lang      (<sub [:language])
        selected  (:inspection/inspector-qualifications @*inspection)
        editable? (can-edit? :inspection/inspector-qualifications)]
    [ui-config-pills/config-pills
     (util/mmap {:id              "inspection-edit-qualifications"
                 :val->str        #(inspection-model/inspector-qualification-name % lang)
                 :val->id         :db/id
                 :values          (<sub [::group-subs/available-qualifications authority-group-id])
                 :add-button-text (tr [:inspection/add-requirement])
                 :selected        (when editable? selected)
                 :pre-selected    (when (not editable?) selected)
                 :legend-text     (tr [:inspection.template/qualification])
                 :add-item        #(update-fn :inspection/inspector-qualifications (conj selected %))
                 :remove-item     #(update-fn :inspection/inspector-qualifications
                                              (->> selected
                                                   (remove (comp (partial = (:db/id %)) :db/id))))}
                tr)]))

(defn editor
  "Inspection editor. Please provide
  `props` with
    `:tr`
    `:on-update`,      a (fn [inspection-id attr new-value])
    `:target-entity`,  execution or application
    `:on-remove`,      optional (fn [inspection-id]) that is called *in addition to*
                       sending the inspection removal signal.
  `inspection`, the inspection to editor
  & `children`, additional content that goes together with the remove button"
  [{:keys [on-update target-entity]
    :as   props}
   inspection
   & children]
  (r/with-let [og-descr    (i18n-model/strings-map (:inspection/descriptions inspection)
                                                   (:inspection/description inspection))
               *inspection (r/atom (-> inspection
                                       (select-keys (concat inspection-model/inspection-editable-keys
                                                            [:db/id
                                                             :inspection/name
                                                             :inspection/names]))
                                       (assoc :inspection/descriptions og-descr)))]
    (let [dummy-signal-fn              #((update-signal-fn inspection %) nil)
          can-edit?                    #(<sub [::process/signal-permitted? target-entity (dummy-signal-fn %)])
          tr                           (:tr props)
          update-fn                    (fn [attr value]
                                         (swap! *inspection assoc attr value)
                                         (on-update (:db/id @*inspection) attr value))
          authority-group              (inspection-model/authority-group inspection)
          allowed-locales              (set (<sub [::ws-subs/allowed-locales]))
          inspection-priority-enabled? (<sub [::ff/enabled? :feature-flag.id/inspection-priority])
          desc-i18n-change?            (->> @*inspection
                                            :inspection/descriptions
                                            (cd/diff og-descr)
                                            second
                                            keys
                                            set
                                            (empty-or-= allowed-locales)
                                            not)]
      [:div
       [ui-i18n/edit-language-selector {:allowed-locales-sub ::ws-subs/allowed-locales}]
       (when (and inspection-priority-enabled?
                  (group-model/has-feature? authority-group :group.feature/inspection-priority))
         [:div.my-4
          (into [ui/select-pills {:label-text (tr [:inspection/priority])
                                  :value      (-> @*inspection :inspection/priority)
                                  :id         "priority-selector"
                                  :disabled   (not (can-edit? :inspection/priority))
                                  :on-change  #(update-fn :inspection/priority %)}]
                (for [priority inspection-model/inspection-priorities]
                  {:value priority
                   :text  (tr [priority])}))])
       [ui-i18n/localized-string-edit (i18n-editor-props props *inspection :inspection/descriptions)]
       (when desc-i18n-change?
         [:div.my-4 [ui/help (tr [:inspection/language-change-note])]])
       (when (<sub [::inspection-sub/can-have-recurring-inspections? (:db/id target-entity)])
         [:<>
          [uic/form-field-wide
           (cc/checkbox
             {:id         "inspection-edit-recurring"
              :label-text (tr [:inspection.template.kind/recurring?])
              :checked    (-> @*inspection :inspection/recurring? boolean)
              :on-change  #(update-fn :inspection/recurring? %)})]
          (when (:inspection/recurring? @*inspection)
            [uic/form-field-wide
             [ui-select/select
              {:values       inspection-model/inspection-recurrence-options
               :id           "inspection-edit-recurrence-interval"
               :label-text   (tr [:inspection.template.kind/recurrence-interval])
               :val->str     #(if (= 1 %)
                                (tr [:select/a-month])
                                (tr [:select/n-months] [%]))
               :on-change    #(update-fn :inspection/recurrence-interval %)
               :value        (-> @*inspection :inspection/recurrence-interval)
               :allow-empty? false}]])])
       [uic/form-field
        [ui/date-picker (-> (editor-props-base props inspection :inspection/due-date)
                            (assoc :callback #(update-fn :inspection/due-date %)
                                   :disabled? (not (can-edit? :inspection/due-date))))]]
       (when (contains? ptc-model/permit-types-with-qualifications
                        (-> target-entity :entity/parent :workspace/permit-type))
         [:div
          [uic/form-field
           [qualification-selector-v2 tr *inspection (:db/id authority-group) can-edit? update-fn]]])
       (into [:div.mt-5.flex
              [remove-button props @*inspection]]
             children)])))
